import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiService, ServiceType } from "./apiServices";
import { SENDER_V3_ENDPOINTS } from './apiEndPoints';
import { senderKeys } from './senderApi';
import { userV3Keys } from './userApi3';

// Types

// Create/Update shipment shared shapes
export interface ShipmentReceiver {
  name: string;
  email: string;
  contact_number: string;
}

export interface ShipmentItem {
  category_id: string;
  image_ids: string[];
}

// Create shipment request
export interface CreateShipmentRequest {
  name: string;
  user_id: string;
  departure_airport_id: string;
  destination_airport_id: string;
  corridor_id: string;
  shipping_start_date: string;
  shipping_end_date: string;
  total_weight: number;
  total_price: number;
  description?: string;
  receiver: ShipmentReceiver;
  items: ShipmentItem[];
}

// Update shipment request
export interface UpdateShipmentRequest {
  id: string;
  user_id: string;
  name: string;
  departure_airport_id: string;
  destination_airport_id: string;
  corridor_id: string;
  shipping_start_date: string;
  shipping_end_date: string;
  total_weight: number;
  total_price: number;
  description?: string;
  receiver: ShipmentReceiver;
  items: ShipmentItem[];
  delete_clothing_Ids?: string[];
  delete_document_Ids?: string[];
  delete_electronics_Ids?: string[];
  delete_other_Ids?: string[];
}

// Sender sends offer to traveler (initiates offer)
export interface SendOfferRequest {
  shipment_id: string;
  itinerary_id: string;
  expiration_duration?: number;
  allocated_price: number;
  sender_platform_fee_percentage: number;
  traveler_platform_fee_percentage: number;
  sender_platform_fee: number;
  traveler_platform_fee: number;
  success_url: string;
  cancel_url: string;
}

// Sender accepts traveler's offer request
export interface AcceptOfferRequestBySender {
  order_code: string;
  success_url: string;
  cancel_url: string;
}

// Sender rejects traveler's offer request
export interface RejectOfferRequestBySender {
  order_code: string;
  reason?: string;
}

// Receiver checkout request
export interface ReceiverCheckoutRequest {
  order_code: string;
  check_out_by: string;
  signature: string;
  comment?: string;
}

// Search travellers (authenticated)
export interface SearchTravellersRequest {
  sender_id: string;
  fromLocation: number;
  toLocation?: number;
  startDate: string;
  endDate: string;
  latitude: number;
  longitude: number;
  radius: number;
  min_weight?: number;
  max_weight?: number;
  sortBy?: string;
  page: number;
  limit: number;
}

// Search travellers (public, unauthenticated)
export interface SearchPublicTravellersRequest {
  fromLocation: number;
  toLocation?: number;
  startDate: string;
  endDate: string;
  latitude: number;
  longitude: number;
  radius: number;
  min_weight?: number;
  max_weight?: number;
  page: number;
  limit: number;
  sortBy?: string;
  currency_code?: string;
}

// Send OTP to receiver
export interface SendOtpRequest {
  order_code: string;
  delivery_type: string;
  email?: string;
  phone_number?: string;
}

// Verify OTP code
export interface VerifyOtpRequest {
  order_code: string;
  otp_code: string;
}

export interface VerifyOtpResponse {
  message: string;
  order_code: string;
  verified_at: string;
  access_token?: string;
  token_type?: string;
  expires_in?: number;
}

// Get 1:1 and group chat room ids + unread counts for an order
export interface OrderChatInfoResponse {
  chat_room_id: string | null;
  unread_messages_count: number;
  group_chat_room_id: string | null;
  group_chat_unread_messages_count: number;
}

// Add rating and review for a traveler
export interface AddTravellerRatingReviewRequest {
  user_id: string;
  reviewer_id: string;
  order_id: string;
  rating: number;
  reason: string;
  comment: string;
}

export interface RatingReviewResponse {
  message: string;
  data: unknown;
}

// Get reviews and ratings for a user
export interface UserReview {
  id: string;
  reviewer_id: string;
  reviewer_name: string;
  reviewer_profile_image: string | null;
  reviewer_role: string;
  rating: number;
  reason: string;
  comment: string;
  order_id: string;
  created_at: string;
}

export interface GetReviewsResponse {
  reviews: UserReview[];
  average_rating: number;
  total_reviews: number;
}

// API functions
const senderApiV3Client = {
  // Base query function
  async request({ url, method = 'GET', body }: { url: string; method?: string; body?: unknown }) {
    try {
      const senderService = getApiService(ServiceType.SENDER3);
      const response = await senderService({
        url,
        method,
        data: body,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: unknown;
        };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  // Create a new shipment
  createShipment: (params: CreateShipmentRequest) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.CREATE_SHIPMENT.path,
      method: 'POST',
      body: params,
    }),

  // Update an existing shipment
  updateShipment: ({ id, ...body }: UpdateShipmentRequest) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.UPDATE_SHIPMENT.path.replace(':id', id),
      method: 'PUT',
      body,
    }),

  // Search uncompleted or cancelled items for a sender by name
  searchItems: (sender_id: string, name?: string) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    const queryParams = params.toString() ? `?${params.toString()}` : '';
    return senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.SEARCH_ITEMS.path.replace(':sender_id', sender_id) + queryParams,
      method: 'GET',
    });
  },

  // Sender sends offer to traveler (accept a traveler order by shipment and itinerary)
  sendOfferRequest: (params: SendOfferRequest) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.SEND_OFFER_REQUEST.path,
      method: 'POST',
      body: params,
    }),

  // Sender accepts traveler's offer request by order code
  acceptOfferRequest: (params: AcceptOfferRequestBySender) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.ACCEPT_OFFER_REQUEST.path,
      method: 'POST',
      body: params,
    }),

  // Sender rejects traveler's offer request by order code
  rejectOfferRequest: (params: RejectOfferRequestBySender) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.REJECT_OFFER_REQUEST.path,
      method: 'POST',
      body: params,
    }),

  // Get pending order requests for a specific shipment
  getPendingRequestsByShipment: (sender_id: string, shipment_id: string) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.GET_PENDING_REQUESTS_BY_SHIPMENT.path
        .replace(':sender_id', sender_id)
        .replace(':shipment_id', shipment_id),
      method: 'GET',
    }),

  // Get my pending order requests for a specific shipment (status = 'pending')
  getMyPendingRequestsByShipment: (sender_id: string, shipment_id: string) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.GET_MY_PENDING_REQUESTS_BY_SHIPMENT.path
        .replace(':sender_id', sender_id)
        .replace(':shipment_id', shipment_id),
      method: 'GET',
    }),

  // Get orders listing for sender (all, under_review, in_progress, completed, cancelled, closed)
  getOrdersListing: (sender_id: string, filter?: string, page = 1, limit = 10) => {
    const params = new URLSearchParams();
    if (filter !== undefined) params.append('filter', filter);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    const queryParams = params.toString() ? `?${params.toString()}` : '';
    return senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.ORDERS_LISTING.path.replace(':sender_id', sender_id) + queryParams,
      method: 'GET',
    });
  },

  // Get order details
  getOrderDetails: (order_id: string) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.ORDER_DETAILS.path.replace(':order_id', order_id),
      method: 'GET',
    }),

  // Get payment history for the authenticated user
  getPaymentHistory: (user_id: string, page = 1, limit = 10) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return senderApiV3Client.request({
      url: `${SENDER_V3_ENDPOINTS.PAYMENT_HISTORY.path.replace(':user_id', user_id)}?${params.toString()}`,
      method: 'GET',
    });
  },

  // Download payment receipt (PDF) for a specific order
  getPaymentReceipt: (order_code: string) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.PAYMENT_RECEIPT.path.replace(':order_code', order_code),
      method: 'GET',
    }),

  // Receiver completes check-out process
  receiverCheckout: (params: ReceiverCheckoutRequest) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.RECEIVER_CHECKOUT.path,
      method: 'POST',
      body: params,
    }),

  // Get all shipment items for user
  getShipmentListing: (user_id: string, page = 1, limit = 10, sort = 'created_at_desc') => {
    const params = new URLSearchParams();
    params.append('user_id', user_id);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    params.append('sort', sort);
    return senderApiV3Client.request({
      url: `${SENDER_V3_ENDPOINTS.SHIPMENT_LISTING.path}?${params.toString()}`,
      method: 'GET',
    });
  },

  // Get shipment details with items, categories, and user
  getShipmentDetails: (id: string) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.SHIPMENT_DETAILS.path.replace(':id', id),
      method: 'GET',
    }),

  // Search senders based on itineraries details (public, no auth required)
  searchPublicTravellers: (payload: SearchPublicTravellersRequest) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.PUBLIC_TRAVEL_SEARCH.path,
      method: 'POST',
      body: payload,
    }),

  // Search senders based on itineraries details (authenticated)
  searchTravellers: (payload: SearchTravellersRequest) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.TRAVEL_TRIPS.path,
      method: 'POST',
      body: payload,
    }),

  // Verify order code (get order details by order_code)
  verifyOrderCode: (order_code: string) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.VERIFY_ORDERCODE.path.replace(':order_code', order_code),
      method: 'GET',
    }),

  // Send OTP to receiver
  sendOtp: (params: SendOtpRequest) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.SEND_OTP.path,
      method: 'POST',
      body: params,
    }),

  // Verify OTP code
  verifyOtp: (params: VerifyOtpRequest): Promise<VerifyOtpResponse> =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.VERIFY_OTP.path,
      method: 'POST',
      body: params,
    }),

  // Get 1:1 and group chat room ids + unread counts for an order
  getOrderChatInfo: (order_id: string, sender_id: string): Promise<OrderChatInfoResponse> =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.GET_ORDER_CHAT_INFO.path
        .replace(':order_id', order_id)
        .replace(':sender_id', sender_id),
      method: 'GET',
    }),

  // Dismiss the "rate your traveler" prompt for an order
  skipRating: (order_id: string, sender_id: string) =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.SKIP_RATING.path
        .replace(':order_id', order_id)
        .replace(':sender_id', sender_id),
      method: 'POST',
    }),

  // Add rating and review for a traveler
  addTravellerRatingReview: (params: AddTravellerRatingReviewRequest): Promise<RatingReviewResponse> =>
    senderApiV3Client.request({
      url: SENDER_V3_ENDPOINTS.ADD_TRAVELLER_RATING_REVIEW.path,
      method: 'POST',
      body: params,
    }),

  // Get reviews and ratings for a user
  getReviews: (user_id: string, role: 'all' | 'sender' | 'traveler' = 'all'): Promise<GetReviewsResponse> => {
    const params = new URLSearchParams();
    params.append('role', role);
    return senderApiV3Client.request({
      url: `${SENDER_V3_ENDPOINTS.GET_REVIEWS.path.replace(':user_id', user_id)}?${params.toString()}`,
      method: 'GET',
    });
  },
};

// TanStack Query hooks

// Create shipment mutation
export const useCreateShipmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateShipmentRequest) => senderApiV3Client.createShipment(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
      queryClient.invalidateQueries({ queryKey: senderKeys.allCheckItems });
    },
  });
};

// Update shipment mutation
export const useUpdateShipmentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateShipmentRequest) => senderApiV3Client.updateShipment(params),
    onMutate: async (variables: UpdateShipmentRequest) => {
      const { id } = variables;

      await queryClient.cancelQueries({ queryKey: senderKeys.allMyItems });
      await queryClient.cancelQueries({ queryKey: ['sender', 'shipmentDetails', id] });

      const previousMyItems = queryClient.getQueryData(senderKeys.allMyItems);
      const previousShipmentDetails = queryClient.getQueryData(['sender', 'shipmentDetails', id]);

      queryClient.setQueryData(senderKeys.allMyItems, (old: unknown) => {
        const oldData = old as { data?: unknown[] } | undefined;
        if (!oldData?.data) return old;
        return {
          ...oldData,
          data: oldData.data.map((item: unknown) => {
            const itemObj = item as Record<string, unknown>;
            return itemObj.id === id ? { ...itemObj, ...variables } : item;
          }),
        };
      });

      queryClient.setQueryData(['sender', 'shipmentDetails', id], (old: unknown) => {
        if (!old) return old;
        return { ...(old as Record<string, unknown>), ...variables };
      });

      return { previousMyItems, previousShipmentDetails };
    },
    onError: (_error: unknown, variables: UpdateShipmentRequest, context: { previousMyItems?: unknown; previousShipmentDetails?: unknown } | undefined) => {
      const { id } = variables;

      if (context?.previousMyItems) {
        queryClient.setQueryData(senderKeys.allMyItems, context.previousMyItems);
      }
      if (context?.previousShipmentDetails) {
        queryClient.setQueryData(['sender', 'shipmentDetails', id], context.previousShipmentDetails);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
      queryClient.invalidateQueries({ queryKey: ['sender', 'shipmentDetails', variables.id] });
    },
  });
};

// Search items by name query
export const useSearchItemsQuery = (sender_id: string, name?: string, options = {}) =>
  useQuery({
    queryKey: ['senderV3', 'searchItems', sender_id, name] as const,
    queryFn: () => senderApiV3Client.searchItems(sender_id, name),
    enabled: !!sender_id,
    ...options,
  });

// Sender sends offer request mutation
export const useSendOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SendOfferRequest) => senderApiV3Client.sendOfferRequest(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
    },
  });
};

// Sender accepts traveler's offer request mutation
export const useAcceptOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AcceptOfferRequestBySender) => senderApiV3Client.acceptOfferRequest(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
    },
  });
};

// Sender rejects traveler's offer request mutation
export const useRejectOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: RejectOfferRequestBySender) => senderApiV3Client.rejectOfferRequest(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
    },
  });
};

// Get pending order requests by shipment ID query
export const useGetPendingRequestsByShipmentQuery = (
  sender_id: string,
  shipment_id: string,
  options = {}
) =>
  useQuery({
    queryKey: ['senderV3', 'pendingRequests', sender_id, shipment_id] as const,
    queryFn: () => senderApiV3Client.getPendingRequestsByShipment(sender_id, shipment_id),
    enabled: !!sender_id && !!shipment_id,
    ...options,
  });

// Get my pending order requests by shipment ID query (status = 'pending')
export const useGetMyPendingRequestsByShipmentQuery = (
  sender_id: string,
  shipment_id: string,
  options = {}
) =>
  useQuery({
    queryKey: ['senderV3', 'myPendingRequests', sender_id, shipment_id] as const,
    queryFn: () => senderApiV3Client.getMyPendingRequestsByShipment(sender_id, shipment_id),
    enabled: !!sender_id && !!shipment_id,
    ...options,
  });

// Get orders listing for sender
export const useGetOrdersListingQuery = (
  sender_id: string,
  filter?: 'all' | 'in_progress' | 'completed' | 'cancelled' | 'under_review' | 'closed',
  page = 1,
  limit = 10,
  options = {}
) =>
  useQuery({
    queryKey: [...senderKeys.myOrders(Number(sender_id)), 'v3', filter, page, limit] as const,
    queryFn: () => senderApiV3Client.getOrdersListing(sender_id, filter, page, limit),
    enabled: !!sender_id,
    ...options,
  });

// Get order details query
export const useGetOrderDetailsQuery = (order_id: string, options = {}) =>
  useQuery({
    queryKey: ['senderV3', 'orderDetails', order_id] as const,
    queryFn: () => senderApiV3Client.getOrderDetails(order_id),
    enabled: !!order_id,
    ...options,
  });

// Get payment history query
export const useGetPaymentHistoryQuery = (user_id: string, page = 1, limit = 10, options = {}) =>
  useQuery({
    queryKey: ['senderV3', 'paymentHistory', user_id, page, limit] as const,
    queryFn: () => senderApiV3Client.getPaymentHistory(user_id, page, limit),
    enabled: !!user_id,
    ...options,
  });

// Get payment receipt query (PDF download)
export const useGetPaymentReceiptQuery = (order_code: string, options = {}) =>
  useQuery({
    queryKey: ['senderV3', 'paymentReceipt', order_code] as const,
    queryFn: () => senderApiV3Client.getPaymentReceipt(order_code),
    enabled: !!order_code,
    ...options,
  });

// Receiver checkout mutation
export const useReceiverCheckoutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ReceiverCheckoutRequest) => senderApiV3Client.receiverCheckout(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
    },
  });
};

// Get all shipment items for user query
export const useGetShipmentListingQuery = (
  user_id: string,
  page = 1,
  limit = 10,
  sort = 'created_at_desc',
  options = {}
) =>
  useQuery({
    queryKey: [...senderKeys.myItems(Number(user_id)), 'v3', page, limit, sort] as const,
    queryFn: () => senderApiV3Client.getShipmentListing(user_id, page, limit, sort),
    enabled: !!user_id,
    ...options,
  });

// Get shipment details query
export const useGetShipmentDetailsQuery = (id: string, options = {}) =>
  useQuery({
    queryKey: ['senderV3', 'shipmentDetails', id] as const,
    queryFn: () => senderApiV3Client.getShipmentDetails(id),
    enabled: !!id,
    ...options,
  });

// Search travellers (public, unauthenticated) mutation
export const useSearchPublicTravellersMutation = () => {
  return useMutation({
    mutationFn: ({ payload }: { payload: SearchPublicTravellersRequest }) =>
      senderApiV3Client.searchPublicTravellers(payload),
  });
};

// Search travellers (authenticated) mutation
export const useSearchTravellersMutation = () => {
  return useMutation({
    mutationFn: ({ payload }: { payload: SearchTravellersRequest }) =>
      senderApiV3Client.searchTravellers(payload),
  });
};

// Verify order code query
export const useVerifyOrderCodeQuery = (order_code: string, options = {}) =>
  useQuery({
    queryKey: ['senderV3', 'verifyOrderCode', order_code] as const,
    queryFn: () => senderApiV3Client.verifyOrderCode(order_code),
    enabled: !!order_code,
    ...options,
  });

// Send OTP mutation
export const useSendOtpMutation = () => {
  return useMutation({
    mutationFn: (params: SendOtpRequest) => senderApiV3Client.sendOtp(params),
  });
};

// Verify OTP mutation
export const useVerifyOtpMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: VerifyOtpRequest) => senderApiV3Client.verifyOtp(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });

      if (data?.access_token) {
        localStorage.setItem(
          'receiver_access_token',
          JSON.stringify({
            access_token: data.access_token,
            token_type: data.token_type,
            expires_in: data.expires_in,
          })
        );
      }
    },
  });
};

// Get 1:1 and group chat room ids + unread counts for an order query
export const useGetOrderChatInfoQuery = (order_id: string, sender_id: string, options = {}) =>
  useQuery<OrderChatInfoResponse>({
    queryKey: ['senderV3', 'orderChatInfo', order_id, sender_id] as const,
    queryFn: () => senderApiV3Client.getOrderChatInfo(order_id, sender_id),
    enabled: !!order_id && !!sender_id,
    ...options,
  });

// Skip rating mutation
export const useSkipRatingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ order_id, sender_id }: { order_id: string; sender_id: string }) =>
      senderApiV3Client.skipRating(order_id, sender_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
    },
  });
};

// Add rating and review for a traveler mutation
export const useAddTravellerRatingReviewMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AddTravellerRatingReviewRequest) => senderApiV3Client.addTravellerRatingReview(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
      // Refetch latest orders so is_rated_by_sender updates and the rating prompt doesn't reappear
      queryClient.invalidateQueries({ queryKey: [...userV3Keys.all, 'latestOrders'] });
      queryClient.invalidateQueries({ queryKey: ['senderV3', 'reviews'] });
    },
  });
};

// Get reviews and ratings for a user query
export const useGetReviewsQuery = (
  user_id: string,
  role: 'all' | 'sender' | 'traveler' = 'all',
  options = {}
) =>
  useQuery<GetReviewsResponse>({
    queryKey: ['senderV3', 'reviews', user_id, role] as const,
    queryFn: () => senderApiV3Client.getReviews(user_id, role),
    enabled: !!user_id,
    ...options,
  });
